/**
 * Admin Portal → Codes → API: every partner API key across partners — its codes in the last 24 hours
 * against its daily quota (counted from the codes, exactly as the quota counts them), its state and
 * last use (Codes PR-8, on `GET /api/admin/codes/api-summary`). Never a key, its hash, or who made it.
 *
 * The SAME component sits on a partner's API keys tab, pre-filtered to that partner (`partnerId`).
 * Read-only: making, rotating and revoking a key stay on the partner's API keys tab (admin roles).
 *
 * Web-only (the admin SPA).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminCard, Badge } from '../../../components/admin/ui';
import { fmtDateTime, hint, td, th } from '../partners/PartnersShared';
import { CodesAdmin } from './codesAdminService';
import { CODES_ROUTES, HOUSE_NO_API_NOTE, QUOTA_WARN_FRACTION, quotaLine, type ApiSummary } from './codesAreaLogic';
import { ErrorLine, readModelError } from './CodesArea';

export const ApiKeysSummary: React.FC<{ partnerId?: string; compact?: boolean }> = ({ partnerId, compact = false }) => {
  const [summary, setSummary] = useState<ApiSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    CodesAdmin.apiSummary(partnerId)
      .then((r) => { if (live) setSummary(r); })
      .catch((e) => { if (live) setError(readModelError(e)); });
    return () => { live = false; };
  }, [partnerId]);

  if (error) return <ErrorLine text={error} />;
  if (!summary) return <div style={hint}>Loading API keys…</div>;
  const keys = summary.keys ?? [];
  return (
    <AdminCard style={{ marginBottom: 12 }} data-testid="api-keys-summary">
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, marginBottom: 8 }}>
        <div><div style={hint}>Keys</div><b>{summary.totals.keys}</b> ({summary.totals.active} active)</div>
        <div><div style={hint}>Codes made in 24 h</div><b>{summary.totals.codes_created_24h.toLocaleString()}</b></div>
        <div><div style={hint}>Calls in 24 h</div><b>{summary.totals.calls_24h.toLocaleString()}</b></div>
        <div><div style={hint}>Partner API</div>{summary.partner_api_live ? 'Live' : 'Switched off (keys work once it is live)'}</div>
      </div>
      {!compact && <p style={{ ...hint, marginTop: 0 }}>{HOUSE_NO_API_NOTE} The quota counts the codes a key made in the last 24 hours ({summary.quota_source === 'codes' ? 'from the codes themselves' : 'from the request log'}).</p>}
      {keys.length === 0 ? (
        <div style={hint}>No API keys{partnerId ? ' for this partner' : ''}.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {!partnerId && <th style={th}>Partner</th>}
                <th style={th}>Key</th><th style={th}>State</th><th style={th}>Quota (24 h)</th><th style={th}>Calls (24 h)</th>
                <th style={th}>Limit / min</th><th style={th}>Last used</th><th style={th}>Codes</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  {!partnerId && (
                    <td style={td}><Link to={`/admin/partners/${encodeURIComponent(k.partner_id)}?tab=api`}>{k.partner_name || k.partner_code}</Link></td>
                  )}
                  <td style={td}><code>{k.key_prefix}</code>{k.name ? <div style={hint}>{k.name}</div> : null}</td>
                  <td style={td}>
                    <Badge status={k.state === 'active' ? 'success' : k.state === 'revoked' ? 'error' : 'neutral'}>{k.state.replace(/_/g, ' ')}</Badge>
                    {k.rotated_to_key_id && <div style={hint}>rotated</div>}
                  </td>
                  <td style={{ ...td, color: (k.quota_used_fraction ?? 0) >= QUOTA_WARN_FRACTION ? 'var(--status-warning-text, #92400e)' : undefined }}>
                    {quotaLine(k)}
                  </td>
                  <td style={td}>{k.calls_24h.toLocaleString()}</td>
                  <td style={td}>{k.rate_limit_per_minute}</td>
                  <td style={td}>{fmtDateTime(k.last_used_at)}</td>
                  <td style={td}><Link to={`${CODES_ROUTES.all}?api_key_id=${encodeURIComponent(k.id)}`}>Its codes →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminCard>
  );
};
